import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { isPresent } from 'src/app/core/utils/isPresent';
import {
  DocumentProcessingPagedReviewResponse,
  FlagType,
  ShortReviewClauseResponseItem,
} from 'src/app/modules/documents/services/document-processing-api.service';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { fromDocTypes } from 'src/app/modules/doc-types/store/selectors';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { PositionDto, PositionsApiService } from 'src/app/shared/services/positions-api.service';
import { selectContentLanguages } from 'src/app/shared/store/selectors';
import {
  ClauseProgressViewModel,
  ReviewDocumentProgressViewModel,
  WorkspaceDetailsDto,
  WorkspaceDocumentDto,
} from '../../../../models/workspaces.model';
import {
  selectAnalyzingClauseVariance,
  selectAnalyzingClauseVarianceResponse,
  selectLoadingReviewDataComplete,
  selectNonReferenceDocuments,
  selectPagedReviewDataByDocumentIdMap,
  selectReferenceDocument,
  selectReviewDoneWithErrors,
  selectReviewedPagesByDocumentIdMap,
  selectReviewProgressPercentAllDocuments,
  selectTotalReviewedPagesAllDocuments,
  selectTotalReviewPagesAllDocuments,
  selectTotalReviewPagesByDocumentIdMap,
  selectWorkspaceDetails,
} from '../../../../store/selectors';

@UntilDestroy()
@Component({
  selector: 'app-analyzing-clause-variance-dashboard',
  templateUrl: './analyzing-clause-variance-dashboard.component.html',
  styleUrl: './analyzing-clause-variance-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyzingClauseVarianceDashboardComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isAnalyzingClauseVarianceLoading$ = this.store$.select(selectAnalyzingClauseVariance);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public selectedReferenceDocument: WorkspaceDocumentDto | null = null;
  public reviewDocuments: WorkspaceDocumentDto[] = [];
  public reviewDocumentsProgress: ReviewDocumentProgressViewModel[] = [];
  public clauseProgress: ClauseProgressViewModel[] = [];
  public clauseOccurrenceStats: ClauseOccurrenceStatsViewModel[] = [];
  public reviewDocumentClauses: ReviewDocumentClausesViewModel[] = [];
  public totalReviewPagesAllDocuments = 0;
  public totalReviewedPagesAllDocuments = 0;
  public remainingReviewPagesAllDocuments = 0;
  public reviewProgressPercentAllDocuments = 10;
  public loadingReviewDataComplete = false;
  public reviewDoneWithErrors = false;
  public constants = constants;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly positionsApiService: PositionsApiService,
    private readonly translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.startInitialization();
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectAnalyzingClauseVarianceResponse).pipe(untilDestroyed(this)),
      this.store$.select(selectReferenceDocument).pipe(untilDestroyed(this)),
      this.store$.select(selectNonReferenceDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectPagedReviewDataByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewedPagesByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewPagesByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewPagesAllDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectTotalReviewedPagesAllDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewProgressPercentAllDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectLoadingReviewDataComplete).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewDoneWithErrors).pipe(untilDestroyed(this)),
      this.store$.select(selectContentLanguages).pipe(
        untilDestroyed(this),
        map((languages) => languages ?? []),
      ),
      this.store$.select(fromDocTypes.selectDocTypes).pipe(
        untilDestroyed(this),
        map((docTypes) => docTypes ?? []),
      ),
      this.positionsApiService.getPositions$().pipe(untilDestroyed(this)),
      this.translocoService.langChanges$.pipe(
        untilDestroyed(this),
        startWith(this.translocoService.getActiveLang()),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          workspaceDetails,
          analyzingClauseVarianceResponse,
          selectedReferenceDocument,
          nonReferenceDocuments,
          pagedReviewDataByDocumentId,
          reviewedPagesByDocumentId,
          totalPagesByDocumentId,
          totalReviewPagesAllDocuments,
          totalReviewedPagesAllDocuments,
          reviewProgressPercentAllDocuments,
          loadingReviewDataComplete,
          reviewDoneWithErrors,
          contentLanguages,
          docTypes,
          positions,
          _activeLang,
        ]) => {
          this.workspaceDetails = workspaceDetails;
          this.selectedReferenceDocument = selectedReferenceDocument;
          this.reviewDocuments = nonReferenceDocuments ?? [];
          const playbookClauses = analyzingClauseVarianceResponse?.playbook?.clauses ?? [];
          const clauseOccurrenceStats = this.getClauseOccurrenceStats(
            playbookClauses,
            this.reviewDocuments,
            pagedReviewDataByDocumentId,
          );
          const clauseOccurrenceStatsById = clauseOccurrenceStats.reduce<
            Record<number, ClauseOccurrenceStatsViewModel>
          >((acc, clauseStat) => {
            acc[clauseStat.id] = clauseStat;
            return acc;
          }, {});

          this.reviewDocumentsProgress = this.reviewDocuments.map((document) => {
            const reviewedPages = reviewedPagesByDocumentId[document.id] ?? 0;
            const totalPages = totalPagesByDocumentId[document.id] ?? 0;
            const remainingPages = Math.max(totalPages - reviewedPages, 0);
            const progressPercent = totalPages
              ? Math.min(Math.round((reviewedPages / totalPages) * 100), 100)
              : 0;

            return {
              id: document.id,
              name: document.name,
              typeLabel: this.getDocumentTypeLabel(document.type, docTypes),
              languageLabel: this.getLanguageLabel(document.languageId, contentLanguages),
              positionLabel: this.getPositionLabel(document.userPositionId, positions),
              reviewedPages,
              totalPages,
              remainingPages,
              progressPercent,
            };
          });
          this.clauseOccurrenceStats = clauseOccurrenceStats;
          this.clauseProgress = playbookClauses.map((clause: ShortReviewClauseResponseItem) => {
            const progressPercent = this.getClauseProgressPercent(
              clauseOccurrenceStatsById[clause.id],
            );

            return {
              id: clause.id,
              name: clause.name,
              progressPercent,
              visibleProgressPercent: this.getVisibleProgressPercent(progressPercent),
            };
          });
          this.reviewDocumentClauses = this.reviewDocuments
            .filter((document) => !!pagedReviewDataByDocumentId[document.id])
            .map((document) => ({
              documentId: document.id,
              documentName: document.name,
              clauses: pagedReviewDataByDocumentId[document.id].clauses.map((clause) => ({
                id: clause.id,
                name: clause.header,
                flagType: this.getFlagTypeLabel(clause.flagType),
              })),
            }));

          this.totalReviewPagesAllDocuments = totalReviewPagesAllDocuments;
          this.totalReviewedPagesAllDocuments = totalReviewedPagesAllDocuments;
          this.remainingReviewPagesAllDocuments = Math.max(
            totalReviewPagesAllDocuments - totalReviewedPagesAllDocuments,
            0,
          );
          this.reviewProgressPercentAllDocuments = Math.max(
            Math.min(reviewProgressPercentAllDocuments, 100),
            10,
          );
          this.loadingReviewDataComplete = loadingReviewDataComplete;
          this.reviewDoneWithErrors = reviewDoneWithErrors;
          this.cdr.markForCheck();
        },
      );
  }

  private getClauseOccurrenceStats(
    playbookClauses: ShortReviewClauseResponseItem[],
    reviewDocuments: WorkspaceDocumentDto[],
    pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse>,
  ): ClauseOccurrenceStatsViewModel[] {
    return playbookClauses.map((playbookClause) => {
      const greenFlagTypeOccurrencesCount = reviewDocuments.filter((document) => {
        const reviewClauses = pagedReviewDataByDocumentId[document.id]?.clauses ?? [];

        return reviewClauses.some(
          (reviewClause) =>
            reviewClause.id === playbookClause.id && reviewClause.flagType === FlagType.MostDesired,
        );
      }).length;

      return {
        id: playbookClause.id,
        name: playbookClause.name,
        occurrencesCount: reviewDocuments.length,
        greenFlagTypeOccurrencesCount,
      };
    });
  }

  private getClauseProgressPercent(clauseOccurrenceStats?: ClauseOccurrenceStatsViewModel): number {
    if (!clauseOccurrenceStats?.occurrencesCount) {
      return 0;
    }

    return Math.min(
      Math.round(
        (clauseOccurrenceStats.greenFlagTypeOccurrencesCount /
          clauseOccurrenceStats.occurrencesCount) *
          100,
      ),
      100,
    );
  }

  private getVisibleProgressPercent(progressPercent: number): number {
    return Math.max(Math.min(progressPercent, 100), 0);
  }

  private getDocumentTypeLabel(
    docTypeId: number | undefined,
    docTypes: GetDocumentTypeDto[],
  ): string {
    return docTypes.find((docType) => docType.id === docTypeId)?.name ?? '-';
  }

  private getLanguageLabel(languageId: number | undefined, languages: LanguageDto[]): string {
    return languages.find((language) => language.id === languageId)?.displayName ?? '-';
  }

  private getPositionLabel(positionId: number | undefined, positions: PositionDto[]): string {
    return positions.find((position) => position.id === positionId)?.name ?? '-';
  }

  private getFlagTypeLabel(flagType: FlagType): string {
    switch (flagType) {
      case FlagType.MostDesired:
        return this.translocoService.translate('workspaces.flagTypeMostDesired');
      case FlagType.Unacceptable:
        return this.translocoService.translate('workspaces.flagTypeUnacceptable');
      case FlagType.Fallback:
        return this.translocoService.translate('workspaces.flagTypeFallback');
      case FlagType.Missing:
        return this.translocoService.translate('workspaces.flagTypeMissing');
      default:
        return this.translocoService.translate('workspaces.flagTypeUnknown');
    }
  }
}

interface ReviewDocumentClausesViewModel {
  documentId: string;
  documentName: string;
  clauses: ReviewDocumentClauseViewModel[];
}

interface ReviewDocumentClauseViewModel {
  id: number;
  name: string;
  flagType: string;
}

interface ClauseOccurrenceStatsViewModel {
  id: number;
  name: string;
  occurrencesCount: number;
  greenFlagTypeOccurrencesCount: number;
}
